import ChartTemplate from "@/components/chart-template";
import { CampaignFilterRow } from "@/http/api-types";
import { useTheme } from "@/lib/theme-provider";
import { SVGProps, useMemo } from "react";
import { Layer, Rectangle, ResponsiveContainer, Sankey, Tooltip } from "recharts";
import { SankeyLink, SankeyNode } from "recharts/types/util/types";
import { REASONS_IN_ORDER as DEFAULT_REASONS, PASS_REASONS } from "./reasons";
type SankeyChartProps = {
  rows: CampaignFilterRow[] | undefined;
  className?: string;
};

type Node = {
  name: string;

  label?: string;
  stage?: number;
  hidden?: boolean;
  value?: number;
};

type LinkType = {
  source: number;
  target: number;
  value: number;
  phantom?: boolean;
  isPassFlow?: boolean;
};

const BIDDING_FLOW_TOTAL_REASONS = DEFAULT_REASONS.slice(0, DEFAULT_REASONS.indexOf("all rules matched") + 1);
const REASONS_IN_ORDER = ["Bidding Flow", ...DEFAULT_REASONS];
function aggregatePerReason(rows: CampaignFilterRow[] | undefined) {
  const map = new Map<string, number>();
  if (!rows) return map;
  const total = rows.reduce((acc, row) => acc + row.count, 0);
  map.set("Bidding Flow", total);
  for (const r of rows) {
    if (!REASONS_IN_ORDER.includes(r.reason)) continue;
    map.set(r.reason, (map.get(r.reason) || 0) + r.count);
  }

  let initialNodeValue = 0;
  for (const reason of BIDDING_FLOW_TOTAL_REASONS) {
    initialNodeValue += map.get(reason) || 0;
  }
  map.set("Bidding Flow", initialNodeValue);
  return map;
}

// Sankey charts normally align terminal nodes at the right edge. In order to imitate a staged view,
// we add a phantom terminal node and link all terminal nodes to it with a very small value link.
const PHANTOM_TERMINAL_NAME = "__END__";
const PHANTOM_LINK_VALUE = 1e-6;

function buildSankeyData(rows: CampaignFilterRow[] | undefined) {
  const agg = aggregatePerReason(rows);

  if (!rows?.length) return { nodes: [], links: [] };

  const nodes: Node[] = [];
  const links: LinkType[] = [];

  let initialNodeValue = 0;
  for (const reason of BIDDING_FLOW_TOTAL_REASONS) {
    initialNodeValue += agg.get(reason) || 0;
  }

  nodes.push({ name: "Bidding Flow__stage0", label: "Bidding Flow", stage: 0, value: initialNodeValue });
  let currentParentIndex = 0;

  const passChain = REASONS_IN_ORDER.filter(
    r => r !== "Bidding Flow" && PASS_REASONS.includes(r) && rows.some(row => row.reason === r),
  );
  const lastPass = passChain[passChain.length - 1];

  let allRulesMatchedNodeIndex: number | null = null;
  let allRulesMatchedCount: number | null = null;

  for (const reason of REASONS_IN_ORDER) {
    if (reason === "Bidding Flow") continue;
    if (!rows.some(r => r.reason === reason)) continue;

    const count = agg.get(reason) || 0;
    const nodeIndex = nodes.length;

    // Track "all rules matched" node index and count
    if (reason === "all rules matched") {
      allRulesMatchedNodeIndex = nodeIndex;
      allRulesMatchedCount = count;
      nodes.push({ name: reason, label: reason, value: count });
    } else {
      nodes.push({ name: reason, label: reason });
    }

    const isPassReason = PASS_REASONS.includes(reason);
    links.push({ source: currentParentIndex, target: nodeIndex, value: count, isPassFlow: isPassReason });

    if (isPassReason) {
      currentParentIndex = nodeIndex;
    }
  }

  // Ensure "all rules matched" node value matches its incoming link value
  if (allRulesMatchedNodeIndex !== null && allRulesMatchedCount !== null && nodes[allRulesMatchedNodeIndex]) {
    nodes[allRulesMatchedNodeIndex].value = allRulesMatchedCount;
  }

  const endNodeIndex = nodes.length;
  nodes.push({ name: PHANTOM_TERMINAL_NAME, label: PHANTOM_TERMINAL_NAME, hidden: true });

  const successNodeIndex = nodes.findIndex(n => n.name === lastPass);
  const sources = new Set(links.map(l => l.source));
  nodes.forEach((n, idx) => {
    if (idx === endNodeIndex) return;
    const isSink = !sources.has(idx);
    if (isSink && idx !== successNodeIndex) {
      links.push({ source: idx, target: endNodeIndex, value: PHANTOM_LINK_VALUE, phantom: true });
    }
  });

  return { nodes, links };
}
export interface NodeProps extends Omit<SVGProps<SVGRectElement>, "height" | "width"> {
  height: number;
  width: number;
  payload: SankeyNode;
  index: number;
  x: number;
  y: number;
}
interface CustomNodePayload extends SankeyNode, Node {
  value: number;
}
interface CustomNodeProps {
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
  payload: CustomNodePayload;
}

interface SankeyTooltipItemPayload {
  name?: string;
  value?: number;
  payload?: {
    isPassFlow?: boolean;
    source?: { name: string; label: string; value: number };
    target?: { name: string };
    value?: number;
  };
}
interface SankeyTooltipProps {
  active?: boolean;
  payload?: SankeyTooltipItemPayload[];
}

interface LinkProps {
  sourceX?: number;
  targetX?: number;
  sourceY?: number;
  targetY?: number;
  sourceControlX?: number;
  targetControlX?: number;
  linkWidth?: number;
  index?: number;
  path?: string;
  payload?:
    | (LinkType & { phantom?: boolean; isPassFlow?: boolean })
    | (SankeyLink & { phantom?: boolean; isPassFlow?: boolean });
}

function SankeyChart({ rows, className }: SankeyChartProps) {
  const data = useMemo(() => buildSankeyData(rows), [rows]);
  const aggregated = useMemo(() => aggregatePerReason(rows), [rows]);
  const { resolvedTheme } = useTheme();

  const colors = useMemo(() => getThemeColorsForSVG(), [resolvedTheme]);

  if (!rows) return null;
  if (!data.nodes.length) return <div className={className}>No data</div>;

  const NodeComponent = ({ x, y, width, height, index, payload }: CustomNodeProps) => {
    if (payload?.hidden || payload?.name === PHANTOM_TERMINAL_NAME) {
      return <g data-hidden-node={payload?.name} />;
    }

    const rawName = (payload.label || payload.name || "").replace(/__stage\d+$/, "");

    const MAX_CHARS = 28;
    const WORDS = rawName.split(/\s+/);
    const lines: string[] = [];
    let current = "";
    for (const w of WORDS) {
      if (!current) {
        current = w;
      } else if ((current + " " + w).length <= MAX_CHARS) {
        current += " " + w;
      } else {
        lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);
    const LINE_H = 13; // px

    const blockHeight = lines.length * LINE_H + LINE_H;
    const startY = y + height / 2 - blockHeight / 2 + LINE_H;
    const valueY = startY + lines.length * LINE_H;

    return (
      <Layer key={`CustomNode${index}`}>
        <Rectangle x={x} y={y} width={width} height={height} fill={colors.nodeFill} fillOpacity="1" />
        <text textAnchor="end" fontSize={12} fill={colors.textColor} fillOpacity={0.9}>
          {lines.map((ln, i) => (
            <tspan key={i} x={x - 8} y={startY + i * LINE_H}>
              {ln}
            </tspan>
          ))}
        </text>
        <text textAnchor="end" x={x - 8} y={valueY} fontSize={11} fill={colors.textColor} fillOpacity={0.5}>
          {Intl.NumberFormat("en-US").format(aggregated.get(rawName) || 0)}
        </text>
      </Layer>
    );
  };

  return (
    <ChartTemplate
      table={null}
      timeSplittable={false}
      title="Campaign Filter Reasons Flow"
      description="Green flow shows bidding path; branches are terminal blocking endpoints and processing a request stops when there is a terminal block."
      chart={
        <div className="w-full h-[700px]">
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={data}
              nodePadding={48}
              nodeWidth={16}
              iterations={64}
              margin={{ top: 30, bottom: 30, left: 80, right: 240 }}
              link={LinkComponent}
              node={NodeComponent}
              linkCurvature={0.75}
            >
              <defs>
                <linearGradient id={"passFlowGradient"}>
                  <stop offset="0%" stopColor="rgba(34, 197, 94, 0.7)" />
                  <stop offset="100%" stopColor="rgba(22, 163, 74, 0.5)" />
                </linearGradient>
                <linearGradient id={"terminalFlowGradient"}>
                  <stop offset="0%" stopColor={colors.mutedFgRgba} />
                  <stop offset="100%" stopColor={colors.mutedFgRgba} stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id={"terminalFlowGradientHover"}>
                  <stop offset="0%" stopColor={colors.mutedFgRgbaHover} />
                  <stop offset="100%" stopColor={colors.mutedFgRgbaHover} stopOpacity={0.7} />
                </linearGradient>
              </defs>
              <Tooltip content={<SankeyTooltip />} />
            </Sankey>
          </ResponsiveContainer>
        </div>
      }
    />
  );
}

function SankeyTooltip({ active, payload }: SankeyTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const pl = item?.payload || {};

  const isLink = pl && typeof pl === "object" && pl.source && pl.target && typeof pl.value === "number";
  if (isLink) {
    const sourceName = pl.source?.name;
    const targetName = pl.target?.name;
    const val = pl.value;
    return (
      <div className="rounded-md border bg-background/95 px-3 py-1.5 text-xs shadow max-w-[24rem]">
        <div className="font-medium mb-1">Flow</div>
        <div className="space-y-0.5">
          <div>
            <span className="text-muted-foreground">From:</span> {sourceName}
          </div>
          <div>
            <span className="text-muted-foreground">To:</span> {targetName}
          </div>
          <div>
            <span className="text-muted-foreground">Value:</span> {val}
          </div>
        </div>
      </div>
    );
  }

  if (typeof pl === "object" && "value" in pl && "sourceX" in pl && "targetX" in pl) {
    return (
      <div className="rounded-md border bg-background/95 px-3 py-1.5 text-xs shadow max-w-[24rem]">
        <div className="font-medium mb-1">{item.name}</div>
        <div className="space-y-0.5">
          {Object.entries(pl).map(([key, val]) => (
            <div key={key}>
              <span className="text-muted-foreground">{key}:</span>{" "}
              {typeof val === "number" ? val.toLocaleString?.() || val : String(val)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const name: string = pl.target?.name || item.name || "";
  if (name === PHANTOM_TERMINAL_NAME) return null;
  const reason = item.name || "";
  const blockedCount = item.value;

  const value = pl.value;
  return (
    <div className="rounded-md border bg-background/95 px-3 py-1.5 text-xs shadow max-w-[26rem]">
      <div className="font-medium mb-1">{reason || name}</div>
      <div className="space-y-0.5">
        {blockedCount !== undefined && (
          <div>
            <span className="text-muted-foreground">Count:</span> {blockedCount.toLocaleString?.() || blockedCount}
          </div>
        )}

        {value !== undefined && (
          <div>
            <span className="text-muted-foreground">Value:</span> {value.toLocaleString?.() || value}
          </div>
        )}
      </div>
    </div>
  );
}

const LinkComponent = ({
  sourceX,
  targetX,
  sourceY,
  targetY,
  sourceControlX,
  targetControlX,
  linkWidth,
  index,
  payload,
  path,
}: LinkProps) => {
  if (payload?.phantom) return <g data-hidden-link={index} />; // keep contract

  const isPassFlow = payload?.isPassFlow || false;
  const fillGradient = isPassFlow ? "url(#passFlowGradient)" : "url(#terminalFlowGradient)";
  const hoverColor = isPassFlow ? "rgba(34, 197, 94, 0.8)" : "url(#terminalFlowGradientHover)";

  const d =
    path ||
    `M${sourceX},${sourceY! + linkWidth! / 2}
      C${sourceControlX},${sourceY! + linkWidth! / 2}
        ${targetControlX},${targetY! + linkWidth! / 2}
        ${targetX},${targetY! + linkWidth! / 2}
      L${targetX},${targetY! - linkWidth! / 2}
      C${targetControlX},${targetY! - linkWidth! / 2}
        ${sourceControlX},${sourceY! - linkWidth! / 2}
        ${sourceX},${sourceY! - linkWidth! / 2}
      Z`;
  const handleEnter = (e: React.MouseEvent<SVGPathElement>) => {
    e.currentTarget.setAttribute("fill", hoverColor);
  };
  const handleLeave = (e: React.MouseEvent<SVGPathElement>) => {
    e.currentTarget.setAttribute("fill", fillGradient);
  };
  return (
    <Layer key={`CustomLink${index}`}>
      <path d={d} fill={fillGradient} strokeWidth={0} onMouseEnter={handleEnter} onMouseLeave={handleLeave} />
    </Layer>
  );
};

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

// Theme colors must be resolved to RGB for SVG gradients because SVG <linearGradient>
// doesn't support CSS variables directly. By reading CSS variables (single source of truth
// for the theme system), we maintain theme consistency while working around SVG limitations.

function getThemeColorsForSVG() {
  const style = getComputedStyle(document.documentElement);
  const mutedFgHsl = style.getPropertyValue("--muted-foreground").trim();
  const [h, s, l] = mutedFgHsl.split(/\s+/).map(v => parseFloat(v));
  const [r, g, b] = hslToRgb(h, s, l);

  return {
    nodeFill: `hsl(${style.getPropertyValue("--primary")})`,
    textColor: `hsl(${style.getPropertyValue("--foreground")})`,
    mutedFg: `hsl(${mutedFgHsl})`,
    mutedFgRgba: `rgba(${r}, ${g}, ${b}, 0.5)`,
    mutedFgRgbaHover: `rgba(${r}, ${g}, ${b}, 0.7)`,
  };
}

export default SankeyChart;
